import {
  createMatrixFrom,
  canConvertToMatrix,
  insertColumn,
  isMatrixCell,
} from "@/components/expression/utils/matrix";
import { type KeyboardInputAction } from "@/input/types";
import { cursorIsActive } from "@/models/CursorModel";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

import { globalShortcuts } from "./globalShortcuts";

import type { DocumentModel } from "@/models/DocumentModel";
import type { KeyCombination, Symbol } from "@corca/data";

export const parameterShortcuts = {
  "Shift+Space": {
    ...globalShortcuts.Space,
    action: (doc, options) => {
      const { cursor } = doc;

      if (!cursorIsActive(cursor)) {
        return false;
      }

      if (canConvertToMatrix(cursor.node)) {
        createMatrixFrom(cursor.node, { cols: 2, rows: 1 });
        return true;
      }

      if (isMatrixCell(cursor.node)) {
        insertColumn(cursor.node);
        return true;
      }

      if (
        doc.editor.tiptap &&
        doc.editor.editorState.isTable &&
        !doc.editor.suggestions.activeSuggestion &&
        options.triggerMode !== "after-autosuggest"
      ) {
        doc.editor.tiptap.chain().addColumnAfter().goToNextCell().focus().run();
        return true;
      }

      return globalShortcuts.Space.action(doc, options);
    },
  },
  ",": {
    action: (doc: DocumentModel) => {
      const { cursor } = doc;
      if (!cursorIsActive(cursor)) return false;

      if (!(cursor.node.parent?.is(FunctionNodeModel) || cursor.node.parent?.is(VariableNodeModel)))
        return false;

      const { node, index, nodeAfterCursor } = cursor;

      doc.transact(() => {
        const inline = doc.create(InlineNodeModel);

        doc.editor.resetInput();

        if (index === 0 && nodeAfterCursor) {
          node.insertSiblingBefore(inline);
        } else if (!nodeAfterCursor) {
          node.insertSiblingAfter(inline);
        } else {
          const nextNodes = node.nodes.slice(cursor.index);

          nextNodes.forEach((n) => doc.removeNode(n));
          node.insertSiblingAfter(inline);
          inline.appendChildren(nextNodes);
        }
        cursor.moveTo(inline, 0);
      }, "shortcut:comma");
      return true;
    },
  },
} satisfies Partial<Record<KeyCombination | Symbol, KeyboardInputAction>>;
